import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Phone,
  MapPin,
  Microscope,
  Syringe,
  FileSearch,
  Clock,
  ShieldCheck,
  Check,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT, DOCTOR } from "@/lib/constants";
import usePageTitle from "@/lib/usePageTitle";
import useOpenGraph from "@/lib/useOpenGraph";

const EASE = [0.22, 1, 0.36, 1];

const CLINICAL_BREAKDOWN = [
  {
    id: "what-is-fnac",
    title: "What is FNAC?",
    body: "Fine Needle Aspiration Cytology (FNAC) is a quick, 15-minute OPD procedure in which a very fine needle is used to draw cells from a lump or swelling for microscopic examination. It is virtually painless, requires no surgical cut, and leaves no stitches — most patients walk in and walk out within a single visit.",
    points: ["15-minute OPD procedure", "Virtually painless", "No surgical cut, no stitches"],
  },
  {
    id: "organs-tested",
    title: "What organs & lumps are tested?",
    body: "FNAC is the first-line diagnostic evaluation for accessible swellings across the body. At our Gwalior clinic we routinely aspirate:",
    points: [
      "Thyroid swellings & nodules",
      "Cervical & axillary lymph nodes",
      "Breast lumps",
      "Salivary gland enlargements",
      "Subcutaneous soft tissue swellings",
    ],
  },
  {
    id: "preparation-safety",
    title: "Patient Preparation & Safety",
    body: "No prolonged fasting is needed before an FNAC. The procedure is performed under strict aseptic precautions, carries minimal risk, and recovery is immediate — you can resume normal activity the same day. Blood-thinner use, if any, is reviewed during your consultation.",
    points: ["No prolonged fasting", "Immediate recovery", "Minimal risk profile"],
  },
  {
    id: "turnaround",
    title: "Turnaround Time",
    body: "A rapid provisional evaluation is communicated at the earliest, and a detailed microscopic cytology report — personally signed by Dr. Anubhuti Jain — is delivered within 24–48 hours.",
    points: ["Rapid provisional evaluation", "Detailed report in 24–48 hours", "Consultant-signed reporting"],
  },
];

const FAQS = [
  {
    q: "Where can I get an FNAC test in Gwalior?",
    a: "Mumbai Elite Diagnostics & Clinic offers FNAC testing at Sector 3, Main Fort Road, Vinay Nagar, Gwalior. Unlike commercial labs, the procedure and the microscopic reporting are performed entirely on-site by Dr. Anubhuti Jain, an MD Pathologist (Gold Medalist).",
  },
  {
    q: "Is the FNAC procedure painful?",
    a: "FNAC uses a needle finer than those used for routine blood draws. Most patients describe only a brief pinching sensation. There is no surgical cut, no stitches, and no downtime — it is a 15-minute OPD procedure.",
  },
  {
    q: "Do I need fasting or any preparation before FNAC?",
    a: "No prolonged fasting is required. You can eat and take your regular medicines unless specifically advised otherwise. If you are on blood thinners, mention it during booking so the procedure can be planned safely.",
  },
  {
    q: "Do you perform Thyroid FNAC in Vinay Nagar, Gwalior?",
    a: "Yes. Thyroid swellings and nodules are among the most common FNAC evaluations at our Vinay Nagar clinic, reported using the standard Bethesda system for thyroid cytopathology.",
  },
  {
    q: "How soon will I receive my cytopathology report?",
    a: "A rapid provisional evaluation is provided at the earliest, and the complete microscopic cytopathology report is delivered within 24–48 hours, personally signed by the consultant pathologist.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const SectionLabel = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
    {children}
  </div>
);

export default function FnacServicePage() {
  usePageTitle("FNAC Test in Gwalior");
  useOpenGraph({
    title: "FNAC Test in Gwalior | Expert Cytopathology — Mumbai Elite Diagnostics",
    description:
      "Expert FNAC testing & cytopathology in Gwalior by Dr. Anubhuti Jain (MBBS, MD Pathology). Thyroid FNAC, lymph node & breast lump evaluation in Vinay Nagar — report in 24–48 hours.",
    url: "https://mumbaielitediagnostics.com/services/fnac-test-gwalior",
  });

  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-schema", "fnac-faq");
    el.text = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section
        data-testid="fnac-hero"
        className="bg-white border-b border-[#E2E8F0] relative overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <motion.div
              className="lg:col-span-8"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <SectionLabel>Cytopathology · Fine Needle Aspiration Cytology</SectionLabel>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#0A2540]">
                Expert FNAC Testing &amp; Cytopathology Services in{" "}
                <span className="italic text-[#2E5AA5]">Gwalior</span>
              </h1>
              <p className="mt-6 font-sans text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl">
                Minimally invasive, quick diagnostic evaluation of lumps and
                swellings performed directly by Dr. Anubhuti Jain (MBBS, MD
                Pathology).
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/bookings"
                  data-testid="fnac-book-cta"
                  className="btn-primary"
                >
                  Book FNAC Consultation
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </Link>
                <a
                  href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                  data-testid="fnac-call-cta"
                  className="btn-secondary"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  Call {CONTACT.phone}
                </a>
              </div>
            </motion.div>
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            >
              <div className="border border-[#E2E8F0] bg-[#F8FAFC] p-6">
                <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-3">
                  At a glance
                </div>
                <ul className="space-y-3 font-sans text-sm text-[#0A2540]">
                  {[
                    "15-minute OPD procedure",
                    "Performed by MD Pathologist",
                    "Report within 24–48 hours",
                    CONTACT.appointmentNote,
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <Check size={14} strokeWidth={2} className="mt-0.5 text-[#B08A4A] shrink-0" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Doctor Authority Card ────────────────────────── */}
      <section data-testid="fnac-authority" className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-4"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <div className="relative border border-white/15 p-3">
                <img
                  src={DOCTOR.photo}
                  alt="Dr. Anubhuti Jain — Consultant Pathologist, FNAC specialist in Gwalior"
                  data-testid="fnac-doctor-photo"
                  className="w-full aspect-[4/5] object-cover"
                  loading="lazy"
                />
                <div className="absolute -top-3 left-6 bg-[#B08A4A] px-3 py-1 font-mono text-[10px] uppercase tracking-widest-plus text-white">
                  Gold Medalist
                </div>
              </div>
            </motion.div>
            <motion.div
              className="lg:col-span-8"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            >
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                The Pathologist Difference
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
                Your FNAC never leaves{" "}
                <span className="italic text-[#B08A4A]">expert hands</span>.
              </h2>
              <p className="mt-6 font-sans text-base text-white/70 leading-relaxed max-w-2xl">
                Unlike commercial labs — where a technician draws the sample
                and slides are outsourced to a distant reporting centre — at
                Mumbai Elite Diagnostics &amp; Clinic the FNAC procedure, slide
                smear preparation, and microscopic cytological interpretation
                are handled entirely on-site by an experienced MD Pathologist.
              </p>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 border-t border-l border-white/10">
                {[
                  { icon: Syringe, t: "Procedure", d: "Aspiration performed personally by Dr. Jain" },
                  { icon: Microscope, t: "Smear Preparation", d: "Slides prepared & stained on-site" },
                  { icon: FileSearch, t: "Interpretation", d: "Microscopic reporting by the MD Pathologist" },
                ].map(({ icon: Icon, t, d }) => (
                  <div key={t} className="border-r border-b border-white/10 p-6">
                    <Icon size={20} strokeWidth={1.5} className="text-[#B08A4A] mb-3" />
                    <div className="font-serif text-lg text-white">{t}</div>
                    <div className="mt-1 font-sans text-xs text-white/60 leading-relaxed">{d}</div>
                  </div>
                ))}
              </div>
              <div className="mt-8 font-mono text-[10px] uppercase tracking-widest-plus text-white/50">
                {DOCTOR.name} · {DOCTOR.credentials}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Clinical Breakdown ───────────────────────────── */}
      <section data-testid="fnac-breakdown" className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionLabel>Clinical Breakdown</SectionLabel>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-[#0A2540]">
                Everything to know before your{" "}
                <span className="italic text-[#2E5AA5]">FNAC</span>.
              </h2>
              <p className="mt-5 font-sans text-base text-[#475569] leading-relaxed">
                A transparent look at the procedure, the swellings it
                evaluates, how to prepare, and when you will receive your
                report.
              </p>
            </div>
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <Accordion type="single" collapsible defaultValue="what-is-fnac" data-testid="fnac-accordion">
                {CLINICAL_BREAKDOWN.map((item, i) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    data-testid={`fnac-accordion-${item.id}`}
                    className="border-b border-[#E2E8F0]"
                  >
                    <AccordionTrigger className="py-6 hover:no-underline group">
                      <span className="flex items-baseline gap-4 text-left">
                        <span className="font-serif text-xl text-[#B08A4A]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-serif text-xl md:text-2xl text-[#0A2540] group-hover:text-[#2E5AA5] transition-colors">
                          {item.title}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pl-11">
                      <p className="font-sans text-sm text-[#475569] leading-relaxed">
                        {item.body}
                      </p>
                      <ul className="mt-4 space-y-2">
                        {item.points.map((p) => (
                          <li key={p} className="flex items-start gap-2 font-sans text-xs text-[#0A2540]">
                            <Check size={12} strokeWidth={2} className="mt-0.5 text-[#B08A4A] shrink-0" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ & SEO ────────────────────────────────────── */}
      <section data-testid="fnac-faq" className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <SectionLabel>Patient FAQs</SectionLabel>
              <h2 className="font-serif text-3xl md:text-4xl leading-[1.08] tracking-tight text-[#0A2540]">
                FNAC test in Gwalior —{" "}
                <span className="italic text-[#2E5AA5]">answered</span>.
              </h2>
              <p className="mt-5 font-sans text-sm text-[#475569] leading-relaxed">
                Common questions about FNAC testing, thyroid FNAC in Vinay
                Nagar, and cytopathology reporting in Gwalior.
              </p>
            </div>
            <div className="lg:col-span-8 divide-y divide-[#E2E8F0] border-t border-b border-[#E2E8F0]">
              {FAQS.map((f, i) => (
                <motion.div
                  key={f.q}
                  data-testid={`fnac-faq-item-${i}`}
                  className="py-6"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                >
                  <h3 className="font-serif text-lg md:text-xl text-[#0A2540]">{f.q}</h3>
                  <p className="mt-2 font-sans text-sm text-[#475569] leading-relaxed">{f.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Location & Booking Banner ────────────────────── */}
      <section data-testid="fnac-booking-banner" className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
                Visit · Book · Resolve
              </div>
              <h2 className="font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight">
                Book your FNAC in <span className="italic text-[#B08A4A]">Gwalior</span> today.
              </h2>
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 font-sans text-sm text-white/80">
                <span className="flex items-start gap-2" data-testid="fnac-banner-address">
                  <MapPin size={15} strokeWidth={1.5} className="mt-0.5 text-[#B08A4A] shrink-0" />
                  Sector 3, Main Fort Road, Vinay Nagar, Gwalior
                </span>
                <a
                  href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
                  data-testid="fnac-banner-phone"
                  className="flex items-center gap-2 hover:text-[#B08A4A] transition-colors"
                >
                  <Phone size={15} strokeWidth={1.5} className="text-[#B08A4A] shrink-0" />
                  Direct Phone: {CONTACT.phone}
                </a>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A]">
                  <Clock size={13} strokeWidth={1.5} />
                  {CONTACT.appointmentNote}
                </span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Link
                to="/bookings"
                data-testid="fnac-banner-book-cta"
                className="inline-flex items-center justify-center gap-3 bg-[#B08A4A] text-white hover:bg-[#8f6f3a] px-8 py-4 text-xs uppercase tracking-widest-plus transition-all duration-300 border border-[#B08A4A] font-medium"
              >
                Book FNAC Consultation
                <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/services"
                data-testid="fnac-banner-services-link"
                className="inline-flex items-center justify-center gap-3 bg-transparent text-white hover:bg-white/10 px-8 py-4 text-xs uppercase tracking-widest-plus transition-all duration-300 border border-white/30 font-medium"
              >
                <ShieldCheck size={16} strokeWidth={1.5} />
                All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
